/**
 * BasketballStatsCard
 *
 * Gamified basketball career statistics card.
 * Mirrors VolleyballStatsCard structure — reuses vb-* CSS classes for
 * visual consistency; sport-specific copy + metric keys only.
 *
 * Props:
 *   stats  — object returned by teamService.getPlayerCareerStats()
 *   user   — { name, username, ... } from AuthContext
 */
import React, { useMemo } from 'react';
import { basketballShooting, basketballPoints } from '../../../utils/basketballShooting';
import '../../../styles/components/sportStats.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) => (n == null ? '—' : Number(n).toFixed(decimals));
const perGame = (total, matches, dec = 1) =>
  matches > 0 ? fmt(total / matches, dec) : '—';
const perGameNum = (total, matches) => (matches > 0 ? total / matches : null);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
};

// ── Player level ──────────────────────────────────────────────────────────────
const LEVELS = [
  { min: 0,  max: 4,  label: 'ROOKIE',   color: '#94a3b8', next: 5  },
  { min: 5,  max: 14, label: 'AMATEUR',  color: '#4ade80', next: 15 },
  { min: 15, max: 29, label: 'PRO',      color: '#38bdf8', next: 30 },
  { min: 30, max: 49, label: 'ELITE',    color: '#a78bfa', next: 50 },
  { min: 50, max: Infinity, label: 'LEGEND', color: '#fbbf24', next: null },
];
const getLevel = (matches) => {
  const lvl = LEVELS.find((l) => matches >= l.min && matches <= l.max) || LEVELS[0];
  const progress = lvl.next
    ? Math.min(100, ((matches - lvl.min) / (lvl.next - lvl.min)) * 100)
    : 100;
  return { ...lvl, progress };
};

// ── Efficiency donut: True Shooting % simplified ──────────────────────────────
const ShootingDonut = ({ points = 0, fga = 0, fta = 0 }) => {
  const tsAttempts = fga + 0.44 * fta;
  const tsPct = tsAttempts > 0 ? (points / (2 * tsAttempts)) * 100 : 0;
  const displayPct = tsPct.toFixed(1);
  const r = 44; const cx = 56; const cy = 56;
  const circ = 2 * Math.PI * r;
  const filled = circ * Math.min(tsPct / 100, 1);
  const ringColor = tsPct >= 60 ? '#f59e0b' : tsPct >= 45 ? '#38bdf8' : '#6366f1';

  return (
    <div className="vb-donut-wrapper">
      <div className="vb-donut-ring-wrap">
        <svg viewBox="0 0 112 112" className="vb-donut-svg">
          <defs>
            <filter id="bkRingGlow">
              <feGaussianBlur stdDeviation="3" result="blur"/>
              <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13"/>
          <circle cx={cx} cy={cy} r={r} fill="none"
            stroke={ringColor} strokeWidth="13"
            strokeDasharray={`${filled} ${circ - filled}`}
            strokeDashoffset={circ * 0.25}
            strokeLinecap="round"
            filter="url(#bkRingGlow)"/>
          <text x={cx} y={cy - 5} textAnchor="middle" className="vb-donut-main">{displayPct}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" className="vb-donut-sub">TS%</text>
        </svg>
      </div>
      <div className="vb-donut-legend">
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background: ringColor}}/>
          <span className="vb-donut-text">PTS: <strong className="vb-donut-val">{points}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(255,255,255,0.15)'}}/>
          <span className="vb-donut-text">FGA: <strong className="vb-donut-val">{fga}</strong></span>
        </div>
        <div className="vb-donut-row">
          <span className="vb-dot" style={{background:'rgba(72,187,120,0.6)'}}/>
          <span className="vb-donut-text">FTA: <strong className="vb-donut-val">{fta}</strong></span>
        </div>
      </div>
    </div>
  );
};

const StatSpot = ({ icon, label, value }) => (
  <div className="vb-spot">
    <div className="vb-spot__icon">{icon}</div>
    <div className="vb-spot__value">{value}</div>
    <div className="vb-spot__label">{label}</div>
  </div>
);

const TrophyCard = ({ icon, label, value, detail, rank }) => {
  const colors = { gold: '#fbbf24', silver: '#94a3b8', bronze: '#cd7f32' };
  const c = colors[rank] || 'rgba(255,255,255,0.2)';
  return (
    <div className="vb-trophy" style={{'--trophy-color': c}}>
      <div className="vb-trophy__icon">{icon}</div>
      <div className="vb-trophy__body">
        <div className="vb-trophy__label">{label}</div>
        <div className="vb-trophy__value">{value ?? '—'}</div>
        {detail && <div className="vb-trophy__detail">{detail}</div>}
      </div>
      {value != null && <div className="vb-trophy__badge">PB</div>}
    </div>
  );
};

const BarRow = ({ label, value, rawNum, maxRef }) => {
  const pct = rawNum != null && maxRef > 0 ? Math.min(100, (rawNum / maxRef) * 100) : 0;
  return (
    <div className="vb-bar-row">
      <div className="vb-bar-header">
        <span className="vb-bar-label">{label}</span>
        <span className="vb-bar-val">{value}</span>
      </div>
      <div className="vb-bar-track">
        <div className="vb-bar-fill" style={{width: `${pct}%`}}/>
      </div>
    </div>
  );
};

const MatchChip = ({ match }) => {
  const pts    = basketballPoints(match.metrics || {}, match.scoreContribution);
  const ast    = match.metrics?.assist || 0;
  const reb    = (match.metrics?.offensiveRebound || 0) + (match.metrics?.defensiveRebound || 0);
  const dunks  = match.metrics?.dunk || 0;
  const date   = formatDate(match.matchDate);
  const resultIcon = match.result === 'win' ? '🏆' : match.result === 'loss' ? '❌' : match.result === 'draw' ? '➖' : null;
  const displayTitle = match.matchTitle || (match.matchShortCode ? `#${match.matchShortCode}` : null) || `Match ${match.matchId}`;
  return (
    <div className="vb-match-chip">
      <div className="vb-match-chip__header">
        <div className="vb-match-chip__title">{displayTitle}</div>
        {resultIcon && <span className="vb-match-chip__result">{resultIcon}</span>}
      </div>
      <div className="vb-match-chip__date">{date || '—'}</div>
      <div className="vb-match-chip__stats">
        <span className="vb-chip-stat"><span className="vb-chip-ico">🏀</span>{pts}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🎯</span>{ast}</span>
        <span className="vb-chip-stat"><span className="vb-chip-ico">🔃</span>{reb}</span>
        {dunks > 0 && (
          <span className="vb-chip-stat vb-chip-pts"><span className="vb-chip-ico">🔥</span>{dunks}</span>
        )}
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const BasketballStatsCard = ({ stats = {}, user = {}, sportProfile = {} }) => {
  const {
    careerTotals  = {},
    careerHighs   = {},
    matchesPlayed  = 0,
    winsCount      = 0,
    lossesCount    = 0,
    drawsCount     = 0,
    scoreContribution = 0,
    recentMatches  = [],
  } = stats;

  // Real points (made shots by value), not one per scoring tap (PR-B13)
  const shooting = basketballShooting(careerTotals);
  const points  = basketballPoints(careerTotals, scoreContribution);
  const assists = careerTotals.assist  || 0;
  const oReb    = careerTotals.offensiveRebound || 0;
  const dReb    = careerTotals.defensiveRebound || 0;
  const rebounds= oReb + dReb;
  const steals  = careerTotals.steal   || 0;
  const blocks  = careerTotals.blockShot || 0;
  const turnovers = careerTotals.turnover || 0;
  const fouls   = careerTotals.foul    || 0;

  // Signature
  const dunks      = careerTotals.dunk             || 0;
  const threes     = careerTotals.threePointerMade || 0;
  const andOnes    = careerTotals.andOne           || 0;
  const fastBreaks = careerTotals.fastBreakPoint   || 0;
  const alleyOops  = careerTotals.alleyOop         || 0;

  // Tracked attempts only — FGA used to be invented as points x 2 (PR-B13)
  const fga = shooting.fga;

  const level = getLevel(matchesPlayed);
  const hasStats = matchesPlayed > 0;

  const pps  = perGameNum(points,  matchesPlayed);
  const aps  = perGameNum(assists, matchesPlayed);
  const rps  = perGameNum(rebounds,matchesPlayed);
  const sps  = perGameNum(steals,  matchesPlayed);
  const bps  = perGameNum(blocks,  matchesPlayed);
  const maxBar = Math.max(pps||0, aps||0, rps||0, sps||0, bps||0, 1);

  const high = (key) => {
    const h = careerHighs[key];
    if (!h) return { value: null, detail: null };
    return {
      value:  h.value,
      detail: h.matchDate ? `vs. ${h.matchTitle || 'match'} · ${formatDate(h.matchDate)}` : null,
    };
  };

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim().toUpperCase()
    : (user?.username || 'PLAYER').toUpperCase();

  const astToTov = useMemo(() => {
    if (!turnovers) return assists > 0 ? '∞' : '—';
    return (assists / turnovers).toFixed(2);
  }, [assists, turnovers]);

  return (
    <div className="vb-card">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="vb-hero">
        <div className="vb-hero__bg-orbs" aria-hidden="true">
          <span className="vb-orb vb-orb--1"/>
          <span className="vb-orb vb-orb--2"/>
        </div>
        <div className="vb-hero__ball" aria-hidden="true">🏀</div>
        <div className="vb-hero__info">
          <div className="vb-hero__name">{displayName}</div>
          <div className="vb-hero__sport">Basketball · Career Statistics</div>
          {(sportProfile?.position || sportProfile?.playingStyle) && (
            <div className="vb-hero__profile">
              {sportProfile.position     && <span className="vb-hero__position">📍 {sportProfile.position}</span>}
              {sportProfile.playingStyle && <span className="vb-hero__style">🎮 {sportProfile.playingStyle}</span>}
            </div>
          )}
        </div>
        <div className="vb-hero__meta">
          <div className="vb-level-badge" style={{'--lv-color': level.color}}>
            <span className="vb-level-badge__label">LEVEL</span>
            <span className="vb-level-badge__name">{level.label}</span>
          </div>
          <div className="vb-hero__matches">
            <span className="vb-hero__matches-num">{matchesPlayed}</span>
            <span className="vb-hero__matches-label">{' '}Game{matchesPlayed !== 1 ? 's' : ''} Played</span>
          </div>
          {(winsCount > 0 || lossesCount > 0) && (
            <div className="vb-hero__record">
              <span className="vb-hero__win">🏆 {winsCount}W</span>
              <span className="vb-hero__sep"> · </span>
              <span className="vb-hero__loss">❌ {lossesCount}L</span>
              {drawsCount > 0 && <><span className="vb-hero__sep"> · </span><span className="vb-hero__draw">➖ {drawsCount}D</span></>}
            </div>
          )}
        </div>
        <div className="vb-xp-bar-wrap">
          <div className="vb-xp-bar-track">
            <div className="vb-xp-bar-fill" style={{width: `${level.progress}%`}}/>
          </div>
          <span className="vb-xp-label">
            {level.next ? `${matchesPlayed} / ${level.next} games to ${LEVELS.find(l => l.min === level.next)?.label || 'MAX'}` : '🏆 MAX LEVEL'}
          </span>
        </div>
      </div>

      {/* ── SIGNATURE STAT ROW ───────────────────────────────────────────── */}
      {hasStats && (dunks + threes + andOnes + fastBreaks + alleyOops) > 0 && (
        <div className="vb-spots-row" style={{borderBottom: '1px solid rgba(251,191,36,0.2)'}}>
          {dunks > 0      && <StatSpot icon="🔥" label="DUNKS"       value={dunks}/>}
          {threes > 0     && <StatSpot icon="🎯" label="3PM"          value={threes}/>}
          {andOnes > 0    && <StatSpot icon="💪" label="AND-1s"       value={andOnes}/>}
          {fastBreaks > 0 && <StatSpot icon="⚡" label="FAST BREAK"  value={fastBreaks}/>}
          {alleyOops > 0  && <StatSpot icon="🙌" label="ALLEY-OOPS"  value={alleyOops}/>}
        </div>
      )}

      {/* ── CORE STAT SPOTLIGHTS ─────────────────────────────────────────── */}
      <div className="vb-spots-row">
        <StatSpot icon="🏀" label="POINTS"   value={hasStats ? points   : '—'}/>
        <StatSpot icon="🎯" label="ASSISTS"  value={hasStats ? assists  : '—'}/>
        <StatSpot icon="🔃" label="REBOUNDS" value={hasStats ? rebounds : '—'}/>
        <StatSpot icon="🛡️" label="STEALS"   value={hasStats ? steals   : '—'}/>
        <StatSpot icon="🧱" label="BLOCKS"   value={hasStats ? blocks   : '—'}/>
        <StatSpot icon="📊" label="A/T RATIO" value={hasStats ? astToTov : '—'}/>
      </div>

      <div className="vb-card__body">
        {/* LEFT col */}
        <div className="vb-card__left">
          <h4 className="vb-section-title">🥇 CAREER HIGHS</h4>
          <div className="vb-trophies">
            <TrophyCard icon="🏀" label="POINTS"    rank="gold"   {...high('points')}/>
            <TrophyCard icon="🎯" label="ASSISTS"   rank="silver" {...high('assist')}/>
            <TrophyCard icon="🔃" label="REBOUNDS"  rank="bronze" value={
              (careerHighs.offensiveRebound?.value||0) + (careerHighs.defensiveRebound?.value||0) || null
            } detail={null}/>
            <TrophyCard icon="🛡️" label="STEALS"    rank="gold"   {...high('steal')}/>
            <TrophyCard icon="🧱" label="BLOCKS"    rank="silver" {...high('blockShot')}/>
            <TrophyCard icon="🔥" label="DUNKS"     rank="bronze" {...high('dunk')}/>
          </div>
        </div>

        {/* RIGHT col */}
        <div className="vb-card__right">
          <h4 className="vb-section-title">📊 TRUE SHOOTING %</h4>
          <ShootingDonut points={points} fga={fga} fta={shooting.fta}/>

          <h4 className="vb-section-title vb-section-title--mt">📈 PER-GAME AVERAGES</h4>
          <div className="vb-bars">
            <BarRow label="Points / Game"  value={perGame(points,  matchesPlayed)} rawNum={pps} maxRef={maxBar}/>
            <BarRow label="Assists / Game" value={perGame(assists, matchesPlayed)} rawNum={aps} maxRef={maxBar}/>
            <BarRow label="Rebounds / Game"value={perGame(rebounds,matchesPlayed)} rawNum={rps} maxRef={maxBar}/>
            <BarRow label="Steals / Game"  value={perGame(steals,  matchesPlayed)} rawNum={sps} maxRef={maxBar}/>
            <BarRow label="Blocks / Game"  value={perGame(blocks,  matchesPlayed)} rawNum={bps} maxRef={maxBar}/>
          </div>

          <div className="vb-totals-row">
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(points)}</span><span className="vb-total-lbl">Total Pts</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(rebounds)}</span><span className="vb-total-lbl">Total Reb</span></div>
            <div className="vb-total-chip"><span className="vb-total-val">{fmt(fouls)}</span><span className="vb-total-lbl">Fouls</span></div>
          </div>
        </div>
      </div>

      {/* ── RECENT MATCHES ───────────────────────────────────────────────── */}
      {recentMatches.length > 0 && (
        <div className="vb-recent">
          <h4 className="vb-section-title vb-section-title--center">🕹️ RECENT GAMES</h4>
          <div className="vb-match-list">
            {recentMatches.slice(0, 5).map((m) => (
              <MatchChip key={m.matchId} match={m}/>
            ))}
          </div>
        </div>
      )}

      {/* ── EMPTY STATE ──────────────────────────────────────────────────── */}
      {!hasStats && (
        <div className="vb-empty">
          <div className="vb-empty__ball">🏀</div>
          <div className="vb-empty__heading">YOUR JOURNEY BEGINS HERE</div>
          <p className="vb-empty__sub">No advanced scoring stats recorded yet.</p>
          <p className="vb-empty__cta">
            Play a live match in <strong>Advanced Scoring</strong> mode to start
            unlocking your basketball career statistics.
          </p>
          <div className="vb-empty__unlock-chips">
            <span className="vb-unlock-chip">🏀 Track Points</span>
            <span className="vb-unlock-chip">🔥 Track Dunks</span>
            <span className="vb-unlock-chip">🎯 Track 3PM</span>
            <span className="vb-unlock-chip">📊 True Shooting %</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasketballStatsCard;
